import { cloneDeep, uniqBy } from "lodash";

import type {
  CalcExpr,
  LumpSpecification,
  SelectExpr,
} from "../../../src/semantic-layer/generated/hex_sl_schema.js";
import { nullthrows } from "../../assertions.js";
import { ColumnFilter } from "../../display-table/filterTypes.js";
import { assertNever } from "../../errors.js";
import {
  CHART_FOLD_KEYS,
  COUNT_STAR_ARG,
} from "../../explore/exploreConstants.js";
import {
  ExploreFieldId,
  ExploreFieldType,
  ExploreSeriesId,
  ExploreSpec,
  ExploreTimeUnit,
  getExploreTimeUnitName,
} from "../../explore/types.js";
import {
  FilterHqlOptions,
  addFiltersToHqlBuilder,
} from "../../filter/hql/filterHql.js";
import { addDetailFieldsToHqlBuilder } from "../../hql/detailFieldsHql.js";
import {
  HqlNextSpecBuilder,
  HqlSelectExprBuilder,
} from "../../hql/HqlNextSpecBuilder.js";
import { joinDatasetQueryPath } from "../../hql/hqlNextUtils.js";
import {
  CalciteType,
  DisplayTableColumnId,
  ExploreCustomTimeUnit,
  HEX_HIDDEN_PREFIX,
  HqlAggregationFunction,
  SemanticDatasetName,
  getExploreFieldName,
  getLumpPredicateString,
  hqlAggToChartAgg,
  partialUuid,
  resolveLumpOrderBy,
} from "../../index.js";
import { notNullish } from "../../filterGuards.js";
import { getDataframeName } from "../../sql/dataSourceTableConfig.js";
import { concatRespectingCase } from "../../utils/string.js";
import { typedObjectValues } from "../../utils/typedObjects.js";
import { aggregateAlwaysPossible } from "../chartConstants.js";
import { getChartSeriesValuesDataFrameColumns } from "../chartDataFrameUtils.js";
import { getChartTooltipResolve } from "../chartTooltipUtils.js";
import { isChartConfiguredEnoughForRender } from "../chartValidationUtils.js";
import {
  ChartAggregate,
  ChartAxisWithData,
  ChartBin,
  ChartDataType,
  ChartFacet,
  ChartLayer,
  ChartLump,
  ChartSeries,
  ChartSpec,
  ChartSqlColumnMappings,
  ChartTimeUnit,
  ChartTimeUnitConfig,
} from "../types.js";

import { ChartHqlSpec, ChartHqlSpecs, ChartJoinSpec } from "./chartHqlSpecs.js";

export interface ChartSemanticColumnMetadata {
  name: string;
  columnType: ExploreFieldType;
  queryPath?: SemanticDatasetName[];
  dataType: CalciteType;
}

interface ChartSqlColumn {
  source:
    | "hfacet"
    | "vfacet"
    | "xAxis"
    | "series"
    | "colorCategorical"
    | "colorQuantitative"
    | "opacity"
    | "tooltip";
  name: string;
  nameWithoutQueryPath: string;
  dataType?: ChartDataType;
  semanticType: ExploreFieldType;
  semanticDataset?: string;
  aggregation?: ChartAggregate;
  timeUnit?: ChartTimeUnit | ExploreCustomTimeUnit;
  timeUnitConfig?: ChartTimeUnitConfig;
  facetBin?: { maxBins: number };
  histogramBin?: ChartBin;
  lump?: ChartLump;
  isForcedAggregation?: boolean;
}

const PREAGG_SUFFIX = "_pa";
const DATECAST_PREFIX = "dt_";

// eslint-disable-next-line max-lines-per-function
export function getChartHqlSpecs({
  chartSpec,
  filters = [],
  hqlNextArgs,
}: {
  chartSpec: ChartSpec;
  filters?: ColumnFilter[];
  hqlNextArgs?: {
    baseDataset: string;
    columnMetadata?: Record<string, ChartSemanticColumnMetadata>;
    addDetailFields?: boolean;
    /**
     * This doesn't change the column names generated by the query, but it does
     * update the return `columnMappings` to map from the outputted column name
     * to the expected display table value (which includes aggs/trunc units)
     * rather than just the column name itself. This is necessary since the
     * chart output column names differ from the nested table/pivot column
     * names.
     */
    mapColumnNamesToTableDisplayNames?: boolean;
    exploreSpec?: ExploreSpec;
    limit?: number;
    filterOptions?: FilterHqlOptions;
  };
}): ChartHqlSpecs {
  //TODO(EXPLORE) this function has gotten to be way too long
  const hqlSpecs: ChartHqlSpec[] = [];
  const joinSpecs: ChartJoinSpec[] = [];
  const columnNameMappings: ChartSqlColumnMappings = {};

  const {
    addDetailFields,
    baseDataset,
    columnMetadata,
    exploreSpec,
    limit,
    mapColumnNamesToTableDisplayNames,
  } = hqlNextArgs ?? {
    baseDataset: undefined,
    columnMetadata: {},
    mapColumnNamesToTableDisplayNames: false,
    exploreSpec: undefined,
    addDetailFields: false,
    limit: undefined,
    filterOptions: undefined,
  };

  if (
    chartSpec.type === "concat" ||
    chartSpec.type === "unsupported" ||
    !isChartConfiguredEnoughForRender(chartSpec)
  ) {
    return {
      hqlSpecs,
      joinSpecs,
      columnNameMappings,
      updatedChartSpec: chartSpec,
      ...getChartSeriesValuesDataFrameColumns(chartSpec),
    };
  }

  // clone spec as we may need to modify the spec later
  const updatedChartSpec = cloneDeep(chartSpec);
  const { facet, layers } = updatedChartSpec;

  // facets
  const facetColumns: ChartSqlColumn[] = [];
  if (facet != null) {
    for (const [source, facetDir] of Object.entries({
      vfacet: facet.facetVertical,
      hfacet: facet.facetHorizontal,
    })) {
      if (facetDir?.dataFrameColumn != null) {
        facetColumns.push({
          source: source === "vfacet" ? "vfacet" : "hfacet",
          name: facetDir.dataFrameColumn,
          nameWithoutQueryPath: getColumnNameWithoutQueryPath(
            facetDir.dataFrameColumn,
            columnMetadata,
          ),
          dataType: facetDir.type,
          semanticType: getSemanticType(
            facetDir.dataFrameColumn,
            columnMetadata,
          ),
          semanticDataset: getSemanticDataset(
            facetDir.dataFrameColumn,
            columnMetadata,
          ),
          timeUnit: facetDir.timeUnit,
          timeUnitConfig: facetDir.timeUnitConfig,
          facetBin:
            facetDir.maxBins != null
              ? {
                  maxBins: facetDir.maxBins,
                }
              : undefined,
          lump: facetDir.lump,
        });
      }
    }

    updateSpecColumnNamesInPlace(facetColumns, { type: "facet", value: facet });
  }

  // layers
  for (const layer of layers) {
    const { series: layerSeries, xAxis } = layer;
    const layerSpecs: { seriesSpecName: string; columns: ChartSqlColumn[] }[] =
      [];

    if (layer.dataFrame == null) {
      continue;
    }

    let xAxisColumn: ChartSqlColumn | undefined;
    for (const series of layerSeries) {
      if (xAxis.dataFrameColumn != null) {
        const forceAggregation = shouldForceXAxisAggregation(xAxis, series);
        const fallbackAggregation = forceAggregation ? "min" : undefined;

        xAxisColumn = {
          source: "xAxis",
          name: xAxis.dataFrameColumn,
          nameWithoutQueryPath: getColumnNameWithoutQueryPath(
            xAxis.dataFrameColumn,
            columnMetadata,
          ),
          dataType: xAxis.type,
          semanticType: getSemanticType(xAxis.dataFrameColumn, columnMetadata),
          semanticDataset: getSemanticDataset(
            xAxis.dataFrameColumn,
            columnMetadata,
          ),
          aggregation: xAxis.aggregate ?? fallbackAggregation,
          timeUnit: xAxis.timeUnit,
          timeUnitConfig: xAxis.timeUnitConfig,
          isForcedAggregation: forceAggregation,
          lump: xAxis.lump,
        };

        if (series.type === "histogram") {
          xAxisColumn.histogramBin = series.bin;
        }
      }

      const dataFrameColumns: ChartSqlColumn[] = series.dataFrameColumns.map(
        (column, columnIndex) => {
          return {
            source: "series",
            name: column,
            nameWithoutQueryPath: getColumnNameWithoutQueryPath(
              column,
              columnMetadata,
            ),
            semanticType: getSemanticType(column, columnMetadata),
            semanticDataset: getSemanticDataset(column, columnMetadata),
            dataType: series.axis.type,
            aggregation:
              series.exploreOnlyWideInputAggregations?.[columnIndex] ??
              series.axis.aggregate,
            timeUnit: series.axis.timeUnit,
          };
        },
      );

      const colorDataFrameColumn: ChartSqlColumn | null =
        series.colorDataFrameColumn != null
          ? {
              source: "colorCategorical",
              name: series.colorDataFrameColumn,
              nameWithoutQueryPath: getColumnNameWithoutQueryPath(
                series.colorDataFrameColumn,
                columnMetadata,
              ),
              dataType: "string",
              semanticType: getSemanticType(
                series.colorDataFrameColumn,
                columnMetadata,
              ),
              semanticDataset: getSemanticDataset(
                series.colorDataFrameColumn,
                columnMetadata,
              ),
              lump:
                series.color.type === "series" ? series.color.lump : undefined,
            }
          : null;

      const colorDotDataFrameColumn: ChartSqlColumn | null =
        series.color.type === "dataframe" &&
        series.color.dataFrameColumn != null
          ? {
              source: "colorQuantitative",
              name: series.color.dataFrameColumn,
              nameWithoutQueryPath: getColumnNameWithoutQueryPath(
                series.color.dataFrameColumn,
                columnMetadata,
              ),
              dataType: series.color.dataType,
              semanticType: getSemanticType(
                series.color.dataFrameColumn,
                columnMetadata,
              ),
              semanticDataset: getSemanticDataset(
                series.color.dataFrameColumn,
                columnMetadata,
              ),
              aggregation: series.color.aggregate,
            }
          : null;

      const opacityColumn: ChartSqlColumn | null =
        series.opacity.type === "dataframe" &&
        series.opacity.dataFrameColumn != null
          ? {
              source: "opacity",
              name: series.opacity.dataFrameColumn,
              nameWithoutQueryPath: getColumnNameWithoutQueryPath(
                series.opacity.dataFrameColumn,
                columnMetadata,
              ),
              semanticType: getSemanticType(
                series.opacity.dataFrameColumn,
                columnMetadata,
              ),
              semanticDataset: getSemanticDataset(
                series.opacity.dataFrameColumn,
                columnMetadata,
              ),
              dataType: series.opacity.dataType,
            }
          : null;

      const tooltipColumns: ChartSqlColumn[] = [];
      if (
        series.tooltip?.type === "manual" ||
        series.tooltip?.type === "custom"
      ) {
        series.tooltip.fields.forEach((field) => {
          let aggregation = field.aggregate;
          let timeUnit = undefined;
          let histogramBin = undefined;

          if (series.tooltip?.type === "manual") {
            if (field.dataFrameColumn === xAxisColumn?.name) {
              aggregation = aggregation ?? xAxisColumn.aggregation;
              timeUnit = xAxisColumn.timeUnit;
              if (series.type === "histogram") {
                histogramBin = xAxisColumn.histogramBin;
              }
            } else if (field.dataFrameColumn === dataFrameColumns[0]?.name) {
              aggregation = aggregation ?? dataFrameColumns[0].aggregation;
            } else if (
              field.dataFrameColumn === colorDotDataFrameColumn?.name
            ) {
              aggregation = aggregation ?? colorDotDataFrameColumn.aggregation;
            } else {
              const yAggregate = dataFrameColumns[0]?.aggregation;
              aggregation =
                aggregation ??
                (field.type !== "number"
                  ? undefined
                  : // always use an aggregate for quantitative manual fields
                    // y.agg is a sensible default unless it's count
                    // count is always the same for all fields so it's not useful
                    yAggregate !== "count"
                    ? yAggregate
                    : "sum");
            }
          }
          tooltipColumns.push({
            source: "tooltip",
            name: field.dataFrameColumn,
            nameWithoutQueryPath: getColumnNameWithoutQueryPath(
              field.dataFrameColumn,
              columnMetadata,
            ),
            dataType:
              aggregation != null && !aggregateAlwaysPossible(aggregation)
                ? "number"
                : field.type,
            semanticType: getSemanticType(
              field.dataFrameColumn,
              columnMetadata,
            ),
            semanticDataset: getSemanticDataset(
              field.dataFrameColumn,
              columnMetadata,
            ),
            aggregation,
            timeUnit,
            histogramBin,
          });
        });
      }

      const seriesColumns = [
        ...dataFrameColumns,
        colorDataFrameColumn,
        colorDotDataFrameColumn,
        opacityColumn,
        ...tooltipColumns,
      ]
        .filter(notNullish)
        .filter((c) => c.nameWithoutQueryPath !== CHART_FOLD_KEYS);
      const collected = [...seriesColumns, ...facetColumns, xAxisColumn]
        .filter(notNullish)
        .filter((c) => c.nameWithoutQueryPath !== CHART_FOLD_KEYS);

      const dataframeName = getDataframeName(layer.dataFrame);

      const builder = new HqlNextSpecBuilder(baseDataset ?? dataframeName);

      const selectsByName: Record<
        string,
        {
          expr: SelectExpr;
          exprWithDatetrunc?: SelectExpr; // used if there is a default time unit and no aggregation
          finalColumnName: string;
          lump?: LumpSpecification;
        }
      > = {};
      for (const column of uniqBy(collected, getPreaggColumnName)) {
        if (
          column.semanticType === "MEASURE" ||
          column.nameWithoutQueryPath === COUNT_STAR_ARG
        ) {
          // cannot add measures to projections as they're not scalar
          continue;
        }

        const selectExprBuilder = new HqlSelectExprBuilder(
          column.nameWithoutQueryPath,
        );

        const preaggColumnName = getPreaggColumnName(column);

        const timeUnit = toExploreTruncUnit(column.timeUnit);
        let baseExpr: SelectExpr | undefined;
        // we don't do any type casting if there is a custom time unit
        if (!ExploreCustomTimeUnit.guard(timeUnit)) {
          if (column.dataType === "datetime") {
            selectExprBuilder.datecast();

            if (timeUnit != null) {
              baseExpr = selectExprBuilder.build({
                as: preaggColumnName,
                dataset: column.semanticDataset,
              });
              selectExprBuilder.datetrunc(timeUnit, column.timeUnitConfig);
            }
          } else if (column.dataType === "number") {
            selectExprBuilder.numbercast();
          }
        }

        let lump: LumpSpecification | undefined;
        const resolvedOrderBy = resolveLumpOrderBy(
          column.lump?.orderBy
            ? ExploreFieldId.check(column.lump.orderBy)
            : undefined,
          // color lumps default to order bys within their series
          // x-axis & facet lumps default to first series order bys
          column.source === "colorCategorical"
            ? (series.id as ExploreSeriesId)
            : undefined,
          exploreSpec,
        );
        if (column.lump != null && resolvedOrderBy != null) {
          lump = {
            window_function: column.lump.windowFunction ?? "row_number",
            order_by: resolvedOrderBy,
            order_direction: column.lump.orderDirection,
            predicate: getLumpPredicateString(column.lump.predicate),
            other_label: column.lump.otherLabel,
          };
        }

        /**
         * save a reference to 2 versions of the select expr:
         * 1. expr: the select expr without datetrunc to be used in group by with `time_unit` in the case of any aggregations
         * 2. exprWithDatetrunc: the select expr with datetrunc to be used in case there are no aggregations
         */
        selectsByName[preaggColumnName] = {
          expr:
            baseExpr ??
            selectExprBuilder.build({
              as: preaggColumnName,
              dataset: column.semanticDataset,
              lump,
            }),
          exprWithDatetrunc: baseExpr
            ? selectExprBuilder.build({
                as: preaggColumnName,
                dataset: column.semanticDataset,
              })
            : undefined,
          finalColumnName: getSqlColumnName(column),
        };
      }

      // Add aggregations
      const dedupedColumns = uniqBy(collected, getSqlColumnName);
      const aggregationBuilder = builder.addAggregations();

      // Aliases that exist before processing this series. Any aliases added after this
      // point belong to the current series and should be the only ones considered when
      // adding measure filters.
      const aliasKeysBeforeSeries = new Set(Object.keys(columnNameMappings));

      for (const column of dedupedColumns) {
        const groupByTimeUnit = toExploreTruncUnit(column.timeUnit);
        const customTimeUnit = ExploreCustomTimeUnit.guard(groupByTimeUnit)
          ? column.timeUnit
          : undefined;

        const aggregation = toSqlAggregation(column.aggregation);

        const preaggColumnName = getPreaggColumnName(column);
        const finalColumnName = getSqlColumnName(column);
        columnNameMappings[finalColumnName] = getColumnMappingValue(
          column,
          mapColumnNamesToTableDisplayNames,
          columnMetadata,
        );

        // handle special case count(*) - this can either be our special "count
        // star" measure (which exists in the explore and chart cell), or a
        // "count star" aggregation tied to a specific column (legacy, only on
        // chart cells.)
        const isCountStarMeasure =
          column.nameWithoutQueryPath === COUNT_STAR_ARG;

        const isCountStarOnColumn =
          aggregation === HqlAggregationFunction.Count &&
          column.aggregation === "count";

        if (isCountStarMeasure || isCountStarOnColumn) {
          aggregationBuilder.aggregation({
            expr: { fun: "count", args: [] },
            dataset: column.semanticDataset,
            alias: finalColumnName,
          });
          continue;
        }

        if (column.semanticType === "MEASURE") {
          aggregationBuilder.aggregation({
            measure: column.nameWithoutQueryPath,
            alias: finalColumnName,
            dataset: column.semanticDataset,
          });
          continue;
        }

        const select = nullthrows(
          selectsByName[preaggColumnName],
          `selectExpr should exist for ${preaggColumnName}`,
        );

        if (aggregation != null) {
          aggregationBuilder.aggregation({
            expr: {
              fun: aggregation.toLowerCase() as Lowercase<HqlAggregationFunction>,
              args: [select.expr.expr as CalcExpr],
            },
            dataset: select.expr.dataset,
            alias: finalColumnName,
          });
        } else if (column.facetBin != null) {
          const binStartColumn = concatRespectingCase(
            finalColumnName,
            "_bin_start",
          );
          const binStepColumn = concatRespectingCase(finalColumnName, "_step");
          columnNameMappings[binStartColumn] = binStartColumn;
          columnNameMappings[binStepColumn] = binStepColumn;

          aggregationBuilder.groupby({
            alias: finalColumnName,
            binning: {
              maxbins: column.facetBin.maxBins,
            },
            dataset: select.expr.dataset,
            expr: select.expr.expr as CalcExpr,
            time_unit: getExploreTimeUnitName(
              groupByTimeUnit,
              column.timeUnitConfig,
            ),
          });
        } else if (column.histogramBin != null) {
          // dataFrameColumn histograms are binned already
          if (column.histogramBin.type !== "dataFrameColumn") {
            aggregationBuilder.groupby({
              alias: finalColumnName,
              binning: {
                maxbins:
                  column.histogramBin.type === "count"
                    ? column.histogramBin.value
                    : undefined,
                step:
                  column.histogramBin.type === "size"
                    ? column.histogramBin.value
                    : undefined,
              },
              dataset: select.expr.dataset,
              expr: select.expr.expr as CalcExpr,
            });

            const aggColumnName = concatRespectingCase(
              finalColumnName,
              "_count",
            );
            aggregationBuilder.aggregation({
              expr: { fun: "count", args: [] },
              alias: aggColumnName,
            });

            const binStartColumn = finalColumnName + "_bin_start";
            const binStopColumn = finalColumnName + "_bin_stop";

            columnNameMappings[aggColumnName] = concatRespectingCase(
              column.name,
              "_count",
            );
            columnNameMappings[binStartColumn] = concatRespectingCase(
              column.name,
              "_bin_start",
            );
            columnNameMappings[binStopColumn] = concatRespectingCase(
              column.name,
              "_bin_stop",
            );
            // for hql-next which have different bin naming
            columnNameMappings[finalColumnName] = column.name;
            columnNameMappings[finalColumnName + "_end"] = concatRespectingCase(
              column.name,
              "_bin_end",
            );
            columnNameMappings[finalColumnName + "_step"] =
              concatRespectingCase(column.name, "_step");
          } else {
            aggregationBuilder.aggregation({
              expr: { fun: "count", args: [] },
              alias: finalColumnName + "_count",
            });

            aggregationBuilder.groupby({
              ...select.expr,
              alias: finalColumnName,
            });
          }
        } else {
          // in here preagg column name == final column name
          // don't do any additional casting when custom time unit specified
          if (customTimeUnit == null && column.dataType === "string") {
            select.expr.expr = {
              fun: "coalesce",
              args: [
                {
                  fun: "totext",
                  args: [select.expr.expr as CalcExpr],
                },
                { str: "null" },
              ],
            };
          }

          const resolvedOrderBy = resolveLumpOrderBy(
            column.lump?.orderBy
              ? ExploreFieldId.check(column.lump.orderBy)
              : undefined, // color lumps default to order bys within their series
            // x-axis & facet lumps default to first series order bys
            column.source === "colorCategorical"
              ? (series.id as ExploreSeriesId)
              : undefined,
            exploreSpec,
          );
          if (column.lump != null && resolvedOrderBy != null) {
            aggregationBuilder.groupby({
              ...select.expr,
              alias: finalColumnName,
              lump: {
                window_function: "row_number",
                order_by: resolvedOrderBy,
                order_direction: column.lump.orderDirection,
                predicate: getLumpPredicateString(column.lump.predicate),
                other_label: column.lump.otherLabel,
              },
            });
          } else {
            aggregationBuilder.groupby({
              ...select.expr,
              alias: finalColumnName,
              time_unit: getExploreTimeUnitName(
                groupByTimeUnit,
                column.timeUnitConfig,
              ),
            });
          }
        }
      }

      // build the agg query and see if there were any aggregations.
      // if not, it means that the chart has no aggregations and we
      // should select the expressions directly for an unagg'd chart
      if (filters && filters.length > 0) {
        addFiltersToHqlBuilder({
          builder: aggregationBuilder,
          filters,
          aliasToOriginalColumnName: Object.fromEntries(
            Object.entries(columnNameMappings).filter(
              ([alias]) => !aliasKeysBeforeSeries.has(alias),
            ),
          ),
          options: hqlNextArgs?.filterOptions,
          exploreSpec,
        });
      }

      const shouldAddDetailFields =
        addDetailFields &&
        hqlNextArgs &&
        exploreSpec &&
        exploreSpec.details.fields.length > 0 &&
        exploreSpec.details.enabled;
      if (shouldAddDetailFields) {
        addDetailFieldsToHqlBuilder({
          builder: aggregationBuilder,
          fields: exploreSpec.details.fields,
          spec: exploreSpec,
        });
      }

      if (limit != null) {
        aggregationBuilder.limit(limit);
      }

      aggregationBuilder.buildAggregation({ skipIfNoAggregations: true });
      let hqlNextQuery = builder.build();
      if (hqlNextQuery.query.transforms.length === 0) {
        // no aggregations so build a select style query
        const selectionBuilder = builder.addSelections(false);
        typedObjectValues(selectsByName).forEach(
          ({ expr, exprWithDatetrunc, finalColumnName }) => {
            selectionBuilder.select({
              ...(exprWithDatetrunc ?? expr),
              alias: finalColumnName,
            });
          },
        );

        if (shouldAddDetailFields) {
          addDetailFieldsToHqlBuilder({
            builder: selectionBuilder,
            fields: exploreSpec.details.fields,
            spec: exploreSpec,
          });
        }

        if (filters && filters.length > 0) {
          addFiltersToHqlBuilder({
            builder: selectionBuilder,
            filters,
            aliasToOriginalColumnName: Object.fromEntries(
              Object.entries(columnNameMappings).filter(
                ([alias]) => !aliasKeysBeforeSeries.has(alias),
              ),
            ),
            options: hqlNextArgs?.filterOptions,
            exploreSpec,
          });
        }

        if (limit != null) {
          // ensure the hard row limit is enforced for unaggregated charts as well
          selectionBuilder.limit(limit);
        }

        selectionBuilder.buildSelection();
        hqlNextQuery = builder.build();
      }

      // limit length of dataframe name to the last 40 characters -- sometimes it can be too long
      // and result in CTE names that are too long for postgres (limit is 63 characters).
      // We get uniqueness from the uuid anyways so there's no need to keep the entire dataframe name.
      // N.B it's very important for downstream that this string ends with the same characters as the end of the series id
      // we use this to match up cell outputs to series in chart renderers e.g. in inlineNamedDatasets() and renderChartOutputsToText()
      const newDataFrameName = `${HEX_HIDDEN_PREFIX}${dataframeName.slice(-40)}__${partialUuid(series.id, 4)}`;
      hqlSpecs.push({
        seriesId: series.id,
        name: newDataFrameName,
        ...hqlNextQuery,
      });
      layerSpecs.push({
        seriesSpecName: newDataFrameName,
        columns: dedupedColumns,
      });

      series.dataFrame = newDataFrameName;
      // update spec dataframe and column names in case they've changed
      updateSpecColumnNamesInPlace(seriesColumns, {
        type: "series",
        value: series,
      });
    }

    if (xAxisColumn != null) {
      updateSpecColumnNamesInPlace([xAxisColumn], {
        type: "layer",
        value: layer,
      });
    }

    if (
      layerSpecs.length === 1 ||
      // if there are any forced aggregations on the x-axis we cannot join queries
      // as the number of rows/values for x-axis columns will be different
      layerSpecs.some((ls) =>
        ls.columns.some(
          (c) => c.source === "xAxis" && c.isForcedAggregation === true,
        ),
      ) ||
      getChartTooltipResolve(layerSeries, xAxis) === "independent"
    ) {
      layer.dataFrame =
        // find the non-force-aggregated series spec name
        layerSpecs.find((ls) =>
          ls.columns.every(
            (c) => c.source !== "xAxis" || c.isForcedAggregation !== true,
          ),
        )?.seriesSpecName ?? layerSpecs[0]?.seriesSpecName;
    } else if (layerSpecs.length > 1 && xAxisColumn != null) {
      // limit length of dataframe name to the last 40 characters -- sometimes it can be too long
      // and result in CTE names that are too long for postgres (limit is 63 characters).
      // ~We get uniqueness from the uuid anyways so there's no need to keep the entire dataframe name.~
      // N.B. the above comment is incorrect: layer.id defaults to "layer" so partialUuid doesn't see it as
      // valid and returns the *first* four chars instead of the *last* four chars so this ends with "laye"
      const joinTableName = `${HEX_HIDDEN_PREFIX}${getDataframeName(layer.dataFrame).slice(-40)}__${partialUuid(layer.id, 4)}`;
      layer.dataFrame = joinTableName;

      const columns = uniqBy(
        layerSpecs.flatMap((s) => {
          return s.columns.map((column) => ({
            seriesSpecName: s.seriesSpecName,
            column: getSqlColumnName(column),
          }));
        }),
        (c) => c.column,
      ).map((c) => `${c.seriesSpecName}.${c.column} as ${c.column}`);

      joinSpecs.push({
        name: joinTableName,
        columns,
        tableNames: layerSpecs.map((s) => s.seriesSpecName),
        joinColumn: getSqlColumnName(xAxisColumn),
      });
    }
  }

  // if no specs were generated, there are no queries to run and
  // we should keep the unchanged original chart spec
  if (hqlSpecs.length === 0) {
    return {
      hqlSpecs,
      joinSpecs: [],
      columnNameMappings: {},
      updatedChartSpec: chartSpec,
      ...getChartSeriesValuesDataFrameColumns(chartSpec),
    };
  }

  // EXP-342: can we detect similar queries and optimize to reduce number of queries to run?
  return {
    hqlSpecs,
    joinSpecs,
    columnNameMappings,
    updatedChartSpec,
    ...getChartSeriesValuesDataFrameColumns(updatedChartSpec),
  };
}

function updateSpecColumnNamesInPlace(
  columns: ChartSqlColumn[],
  spec:
    | {
        type: "facet";
        value: ChartFacet;
      }
    | {
        type: "layer";
        value: ChartLayer;
      }
    | {
        type: "series";
        value: ChartSeries;
      },
): void {
  for (const column of columns) {
    if (column.nameWithoutQueryPath === CHART_FOLD_KEYS) {
      continue;
    }
    const newColumnName = getSqlColumnName(column);

    if (spec.type === "facet") {
      switch (column.source) {
        case "vfacet":
          if (spec.value.facetVertical != null) {
            spec.value.facetVertical.dataFrameColumn = newColumnName;
          }
          break;
        case "hfacet":
          if (spec.value.facetHorizontal != null) {
            spec.value.facetHorizontal.dataFrameColumn = newColumnName;
          }
          break;
        case "xAxis":
        case "series":
        case "colorCategorical":
        case "colorQuantitative":
        case "opacity":
        case "tooltip":
          break;
        default:
          assertNever(column.source, column.source);
      }
    } else if (spec.type === "layer") {
      switch (column.source) {
        case "xAxis":
          if (spec.value.xAxis != null) {
            spec.value.xAxis.dataFrameColumn = newColumnName;
          }
          break;
        case "series":
        case "colorCategorical":
        case "colorQuantitative":
        case "opacity":
        case "tooltip":
        case "vfacet":
        case "hfacet":
          break;
        default:
          assertNever(column.source, column.source);
      }
    } else if (spec.type === "series") {
      switch (column.source) {
        case "series":
          spec.value.dataFrameColumns = spec.value.dataFrameColumns.map(
            (oldColumn) => {
              if (oldColumn === column.name) {
                return newColumnName;
              } else {
                return oldColumn;
              }
            },
          );
          break;
        case "colorCategorical":
          spec.value.colorDataFrameColumn = newColumnName;
          break;
        case "colorQuantitative":
          if (spec.value.color.type === "dataframe") {
            spec.value.color.dataFrameColumn = newColumnName;
          }
          break;
        case "opacity":
          if (spec.value.opacity.type === "dataframe") {
            spec.value.opacity.dataFrameColumn = newColumnName;
          }
          break;
        case "tooltip":
          if (
            spec.value.tooltip?.type === "manual" ||
            spec.value.tooltip?.type === "custom"
          ) {
            spec.value.tooltip.fields = spec.value.tooltip.fields.map(
              (field) => {
                if (
                  field.dataFrameColumn === column.name &&
                  (spec.value.tooltip?.type === "manual" ||
                    field.aggregate === column.aggregation)
                ) {
                  return {
                    ...field,
                    dataFrameColumn: newColumnName,
                  };
                } else {
                  return field;
                }
              },
            );
          }
          break;
        case "xAxis":
        case "vfacet":
        case "hfacet":
          break;
        default:
          assertNever(column.source, column.source);
      }
    } else {
      assertNever(spec, spec);
    }
  }
}
function getColumnMappingValue(
  column: ChartSqlColumn,
  mapColumnNamesToTableDisplayNames: boolean | undefined,
  columnMetadata: Record<string, ChartSemanticColumnMetadata> | undefined,
): string {
  if (!mapColumnNamesToTableDisplayNames || columnMetadata == null) {
    return column.name;
  } else {
    return getExploreFieldName({
      value: column.nameWithoutQueryPath,
      aggregation: toSqlAggregation(column.aggregation) ?? undefined,
      truncUnit: toExploreTruncUnit(column.timeUnit) ?? undefined,
      queryPath: columnMetadata[column.name]?.queryPath ?? [],
      fieldType:
        columnMetadata[column.name]?.columnType ?? ExploreFieldType.COLUMN,
    });
  }
}

// In the absence of y-axis and aggregate, force an x-aggregate to
// avoid bringing in too-many-rows.
// This needs to kep in sync with series translators.
function shouldForceXAxisAggregation(
  xAxis: ChartAxisWithData,
  series: ChartSeries,
): boolean {
  return (
    (series.type === "area" ||
      series.type === "line" ||
      series.type === "bar") &&
    xAxis.aggregate == null &&
    series.dataFrameColumns.length === 0
  );
}

function getPreaggColumnName(column: ChartSqlColumn): string {
  return (
    getSqlColumnName({
      ...column,
      facetBin: undefined,
      histogramBin: undefined,
      aggregation: undefined,
    }) + PREAGG_SUFFIX
  );
}

function getSemanticType(
  columnName: string,
  columnMetadata: Record<string, ChartSemanticColumnMetadata> | undefined,
): ExploreFieldType {
  return columnMetadata?.[columnName]?.columnType ?? "COLUMN";
}

function getSemanticDataset(
  columnName: string,
  columnMetadata: Record<string, ChartSemanticColumnMetadata> | undefined,
): string | undefined {
  if (columnMetadata == null) {
    return;
  }

  const metadata = columnMetadata[columnName];
  if (metadata?.queryPath == null || metadata.queryPath.length === 0) {
    return;
  }
  return joinDatasetQueryPath(metadata.queryPath);
}

function getColumnNameWithoutQueryPath(
  columnName: string,
  columnMetadata: Record<string, ChartSemanticColumnMetadata> | undefined,
): string {
  if (columnMetadata == null) {
    return columnName;
  }

  const metadata = columnMetadata[columnName];
  return metadata?.name ?? columnName;
}

// exported for testing
export function getSqlColumnName(column: ChartSqlColumn): string {
  // aliases should satisfy:
  // 1 sum(x) and avg(x) should have different aliases
  // 2 two instances of sum(x) should have the same alias
  // 3 all lowercase to avoid case sensitivity issues in DBs
  // 4 short enough to avoid column name length limits in DBs
  // 5 no special characters to avoid quoting issues
  // 6 starts with a letter to avoid issues with parsers
  // 7 some level of human readability to ease debugging

  // we use `c{hash(modifiers+column.name)}_{modifiers}_{sanitized(column.name)}`,
  // trimming from the left of sanitized(column.name) to fit within the character limit
  // `c{hash}` alone satisfies 1-6 above regardless of special chars in column.name
  // the rest is optional and there for human readability

  // enumerate modifiers on the underlying column
  const modifiers = [];
  if (column.dataType != null) {
    modifiers.unshift(column.dataType);
  }
  // forced aggregations are aggregations that aren't added by the user
  // but by the system automatically, so avoid having it affect the column name
  // to avoid issues (like multiple series using different x-axis columns)
  if (column.aggregation != null && column.isForcedAggregation !== true) {
    modifiers.unshift(column.aggregation);
  }
  if (column.timeUnit != null) {
    if (ExploreCustomTimeUnit.guard(column.timeUnit)) {
      modifiers.unshift(`${column.timeUnit.type}_${column.timeUnit.name}`);
    } else {
      modifiers.unshift(column.timeUnit);
    }
  }

  if (column.facetBin != null) {
    modifiers.unshift(`facet_maxbins_${column.facetBin.maxBins}`);
  }
  if (column.histogramBin != null) {
    if (column.histogramBin.type === "count") {
      modifiers.unshift(
        `hist_maxbins_${(column.histogramBin.value ?? "").toString().replace(".", "_")}`,
      );
    } else if (column.histogramBin.type === "size") {
      modifiers.unshift(
        `hist_step_${(column.histogramBin.value ?? "").toString().replace(".", "_")}`, // e.g. bin value 10.5 -> "hist_step_10_5"
      );
    } else if (column.histogramBin.type === "dataFrameColumn") {
      modifiers.unshift("hist_column");
    }
  }
  if (column.lump != null) {
    modifiers.unshift(
      `lump_${column.lump.predicate.op.toLowerCase()}_${column.lump.predicate.arg}`,
    );
  }

  const modifierStr = modifiers.join("_");
  const hash = hashCode(modifierStr + column.name);

  const required = `c${hash}`; // satisfies 1-6 above regardless of special chars in column.name

  // now append the modifiers for human readability, they're short and should be fine
  const prefix = `${required}_${modifierStr}_`;

  // now fill to the character limit from the back of column.name for human readability
  // from the back since much of the length will tend to come from shared prefixes
  // thus filling from the front would make the names less human-identifiable
  const safeName = column.name.toLowerCase().replace(/[^a-zA-Z0-9_]/g, "");
  const maxChars = 63 - PREAGG_SUFFIX.length - DATECAST_PREFIX.length; // postgres limit minus prefix/suffixes that could be added later
  const availableChars = maxChars - prefix.length;

  const suffix = safeName.slice(-availableChars);

  return `${prefix}${suffix}`.slice(0, maxChars); // truncate to maxChars again to be extra-sure
}

/**
 * Not exactly the opposite of getSqlColumnName.
 *
 * Sometimes, when you aggregate a column e.g. `foo_bar` in explore,
 * the column id becomes `foo_bar_sum`. This function takes `foo_bar_sum`
 * and returns `foo_bar`.
 */
export function removeAggSuffixFromColumnId(
  columnId: DisplayTableColumnId,
  aggregation: HqlAggregationFunction | undefined,
): DisplayTableColumnId {
  if (aggregation == null) {
    return columnId;
  }

  const parts = columnId.split(COLUMN_ID_DELIMITER);
  const lastPart = parts[parts.length - 1];
  const possibleAggSuffixes = new Set(
    aggregation === HqlAggregationFunction.Count
      ? ["valid", "count"] // count can map to both `valid` and `count`
      : [hqlAggToChartAgg(aggregation)],
  );
  if (parts.length >= 2 && lastPart && possibleAggSuffixes.has(lastPart)) {
    return parts.slice(0, -1).join(COLUMN_ID_DELIMITER) as DisplayTableColumnId;
  }
  return columnId;
}
const COLUMN_ID_DELIMITER = "_";

export function toSqlAggregation(
  aggregation?: ChartAggregate,
): HqlAggregationFunction | null {
  if (aggregation == null || aggregation === "preagg") {
    return null;
  }
  switch (aggregation) {
    case "count":
    case "valid": // this is later interpreted as count(*)
      return HqlAggregationFunction.Count;
    case "distinct":
      return HqlAggregationFunction.CountDistinct;
    case "sum":
      return HqlAggregationFunction.Sum;
    case "average":
    case "mean":
      return HqlAggregationFunction.Avg;
    case "min":
      return HqlAggregationFunction.Min;
    case "max":
      return HqlAggregationFunction.Max;
    case "median":
      return HqlAggregationFunction.Median;
    case "stdev":
      return HqlAggregationFunction.StdDev;
    case "stdevp":
      return HqlAggregationFunction.StdDevPop;
    case "variance":
      return HqlAggregationFunction.Variance;
    case "variancep":
      return HqlAggregationFunction.VariancePop;
    case "stderr":
      // TODO not implemented in HQL
      return null;
    case "ci0":
    case "ci1":
    case "missing":
    case "product":
    case "q1":
    case "q3":
    case "values":
      // not exposed in the chart UI but valid Vega
      return null;
    default:
      assertNever(aggregation, aggregation);
  }
}

export function toExploreTruncUnit(
  timeUnit: ChartTimeUnit | ExploreCustomTimeUnit | undefined,
): ExploreTimeUnit | null {
  if (timeUnit == null) {
    return null;
  }

  if (ExploreCustomTimeUnit.guard(timeUnit)) {
    return timeUnit;
  }

  switch (timeUnit) {
    case "year":
      return "year";
    case "yearquarter":
      return "quarter";
    case "yearmonth":
      return "month";
    case "yearweek":
      return "week";
    case "yearmonthdate":
      return "day";
    case "yearmonthdatehours":
      return "hour";
    case "yearmonthdatehoursminutes":
      return "minute";
    case "yearmonthdatehoursminutesseconds":
      return "second";
    case "day":
      return "dayofweek";
    default:
      // eslint-disable-next-line no-console -- helpful for debugging
      console.debug("Unsupported sql gen time unit", timeUnit);
      return null;
  }
}

// hashcode logic mostly from https://stackoverflow.com/a/7616484
function hashCode(s: string): string {
  let hash = 0,
    i,
    chr;
  if (s.length === 0) return `${hash}`;
  for (i = 0; i < s.length; i++) {
    chr = s.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36).slice(0, 6);
}
